import { useCallback, useEffect, useRef, useState } from "react";
import { QuickAccountForm } from "../accounts/QuickAccountForm";
import { callTool } from "../api";
import { StatusBlock } from "../components/StatusBlock";
import { BoothNavButton, resolveMapHall } from "../components/BoothNavButton";
import { FilterPill } from "../components/FilterPill";
import { PriorityPill, SegmentPill } from "../components/SegmentPill";
import { ContinueWalkPrompt, loadMyWalk, putMyWalk, WalkPlanButton } from "../components/WalkPlan";
import { useSpeechToText } from "../hooks/useSpeechToText";
import { accountSupportsWalkPlan } from "../ratings";
import { formatNoteStamp, ratingLineage } from "../ratings";
import type { LoadState } from "../types";
import { displayPriorityLabel } from "../debrief/logic";
import { asDisplayList, asStringList, asText, httpsUrl, parseTechStackView, type TechStackEntry } from "./displayRules";
import { loadCompanyDossier } from "./loadCompanyDossier";
import { CompanyBrowserModal, CompanyBrowserPane } from "./CompanyBrowserModal";
import { shortCompanyChipLabel, usableBoothGroup } from "./sharedBoothGroup";
import type { CompanyBrowserTarget, CompanyDossier, CompanyOpenMode, CompanyTab } from "./types";

function str(record: Record<string, unknown> | null | undefined, key: string): string | null {
  return record ? asText(record[key]) : null;
}

function hostnameFromHref(href: string | null): string | null {
  if (!href) return null;
  try {
    return new URL(href).hostname.replace(/^www\./i, "");
  } catch {
    return href.replace(/^https?:\/\//i, "").replace(/\/.*$/, "");
  }
}

function hallIdForBooth(dossier: CompanyDossier | null, boothNumber: string): string | undefined {
  if (!dossier) return resolveMapHall(undefined, boothNumber);
  const booth = dossier.booths.find((row) => {
    const rec = row as Record<string, unknown>;
    return asText(rec.booth_number) === boothNumber || asText(rec.booth_id) === boothNumber;
  });
  const rec = (booth ?? {}) as Record<string, unknown>;
  const raw = asText(rec.hall_id) || asText(rec.hall_name) || asText(rec.hall) || asText(rec.map_name);
  return resolveMapHall(raw, boothNumber);
}

const COMPANY_SECTION_JUMPS = [
  { id: "services", label: "Services" },
  { id: "people", label: "People" },
  { id: "sites", label: "Sites" },
  { id: "aircraft", label: "Aircraft" },
  { id: "news", label: "News" },
  { id: "notes", label: "Notes" },
  { id: "techstack", label: "Tech stack" },
  { id: "partnerships", label: "Partnerships" },
] as const;

type CompanyJumpId = (typeof COMPANY_SECTION_JUMPS)[number]["id"];

/** Last section jump, kept across record switches (and remounts in this session). */
let lastCompanyJump: CompanyJumpId | null = null;

function isCompanyJumpId(id: string): id is CompanyJumpId {
  return COMPANY_SECTION_JUMPS.some((item) => item.id === id);
}

function coreSectionId(tab?: CompanyTab): string | null {
  if (!tab) return null;
  if (tab === "showkit") return "talktrack";
  if (tab === "campaign") return "people";
  if (tab === "software") return "techstack";
  return tab;
}

function MicIcon() {
  return (
    <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
      <path
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
        d="M12 3.5a3 3 0 0 1 3 3V11a3 3 0 0 1-6 0V6.5a3 3 0 0 1 3-3z"
      />
      <path fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" d="M6.5 11a5.5 5.5 0 0 0 11 0M12 16.5V20" />
    </svg>
  );
}

function scrollToCompanySection(id: string, behavior: ScrollBehavior = "smooth") {
  requestAnimationFrame(() => {
    const el = document.getElementById(`company-section-${id}`);
    if (!el) return;
    const scroller = el.closest(".company-shell");
    if (!(scroller instanceof HTMLElement)) {
      el.scrollIntoView({ behavior, block: "start" });
      return;
    }
    const header = scroller.querySelector(".company-modal-header");
    const headerH = header instanceof HTMLElement ? header.offsetHeight : 0;
    const top =
      el.getBoundingClientRect().top - scroller.getBoundingClientRect().top + scroller.scrollTop - headerH - 8;
    scroller.scrollTo({ top: Math.max(0, top), behavior });
  });
}

export function CompanyBrowserScreen({
  eventKey,
  campaignKey,
  target,
  actor,
  onClose,
  onSetDestination,
  onClosedOut,
  onCheckIn,
  onSwitchAccount,
  onOpenContact,
  variant = "modal",
}: {
  eventKey: string;
  campaignKey: string;
  target: CompanyBrowserTarget;
  actor?: string;
  onClose: () => void;
  onSetDestination?: (booth: string, label: string, hall?: string) => void;
  onClosedOut?: () => void;
  onCheckIn?: (booth: string, hall?: string) => Promise<void> | void;
  onSwitchAccount?: (target: CompanyBrowserTarget) => void;
  onOpenContact?: (target: { contact_id: string }) => void;
  variant?: CompanyOpenMode;
}) {
  const [state, setState] = useState<LoadState<CompanyDossier>>({ status: "loading" });
  const [reloadTick, setReloadTick] = useState(0);
  const [checkInBusy, setCheckInBusy] = useState(false);
  const [checkInError, setCheckInError] = useState<string | null>(null);
  const [checkedIn, setCheckedIn] = useState(false);
  const [walking, setWalking] = useState(false);
  const [walkBusy, setWalkBusy] = useState(false);
  const [walkError, setWalkError] = useState<string | null>(null);
  const [headerMenu, setHeaderMenu] = useState<"segment" | "priority" | null>(null);
  const [classPatchError, setClassPatchError] = useState<string | null>(null);
  const [accountEditOpen, setAccountEditOpen] = useState(false);
  const [accountEditBusy, setAccountEditBusy] = useState(false);
  const [accountEditError, setAccountEditError] = useState<string | null>(null);
  const [accountForm, setAccountForm] = useState({ name: "", domain: "", website: "", secondaryWebsite: "" });
  const [localSegment, setLocalSegment] = useState<string | null>(null);
  const [localPriority, setLocalPriority] = useState<string | null>(null);
  const [localAccountName, setLocalAccountName] = useState<string | null>(null);
  const [activeJump, setActiveJump] = useState<CompanyJumpId | null>(() => lastCompanyJump);
  const restoredJumpForRef = useRef<string | null>(null);

  const rememberJump = useCallback((id: CompanyJumpId) => {
    lastCompanyJump = id;
    setActiveJump(id);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setState((prev) => (prev.status === "ready" ? prev : { status: "loading" }));
    void loadCompanyDossier(eventKey, target, campaignKey, actor)
      .then((data) => {
        if (!cancelled) setState({ status: "ready", data });
      })
      .catch((error) => {
        if (!cancelled) {
          setState((prev) =>
            prev.status === "ready"
              ? prev
              : {
                  status: "error",
                  message: error instanceof Error ? error.message : "company_browser_get failed",
                },
          );
        }
      });
    return () => {
      cancelled = true;
    };
  }, [eventKey, campaignKey, target.account_id, target.exhibitor_id, target.booth_number, actor, reloadTick]);

  useEffect(() => {
    setWalkError(null);
    setClassPatchError(null);
    setHeaderMenu(null);
  }, [target.account_id, target.exhibitor_id, target.booth_number, reloadTick]);

  useEffect(() => {
    setLocalSegment(null);
    setLocalPriority(null);
  }, [target.account_id, target.exhibitor_id, target.booth_number]);

  const dossier = state.status === "ready" ? state.data : null;
  const accountId =
    dossier?.resolution.account_id ?? dossier?.account?.account_id ?? target.account_id ?? null;

  useEffect(() => {
    if (!dossier) return;
    const nextName = asText(dossier.account?.name) || str(dossier.exhibitor, "company_name") || "";
    const nextDomain = asText(dossier.account?.domain) || "";
    const nextWebsite = httpsUrl(dossier.profile?.website) || httpsUrl(str(dossier.exhibitor, "website")) || "";
    const nextSecondary = httpsUrl(dossier.profile?.secondary_website) || "";
    setAccountForm({ name: nextName, domain: nextDomain, website: nextWebsite, secondaryWebsite: nextSecondary });
    setLocalAccountName(null);
    setAccountEditOpen(false);
    setAccountEditError(null);
  }, [dossier]);

  useEffect(() => {
    if (typeof dossier?.my_owner === "boolean") {
      setWalking(dossier.my_owner);
    }
  }, [dossier?.my_owner, accountId]);

  useEffect(() => {
    if (!accountId || !actor) {
      if (!accountId) setWalking(false);
      return;
    }
    let cancelled = false;
    void loadMyWalk(accountId, actor)
      .then((on) => {
        if (!cancelled) setWalking(on);
      })
      .catch((error) => {
        if (!cancelled) {
          setWalkError(error instanceof Error ? error.message : "Walk lookup failed");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [accountId, actor, reloadTick]);

  useEffect(() => {
    if (state.status !== "ready" || !dossier) return;
    const sameAccount = !target.account_id || target.account_id === dossier.resolution.account_id;
    const sameExhibitor = !target.exhibitor_id || target.exhibitor_id === dossier.resolution.exhibitor_id;
    const sameBooth =
      !target.booth_number || dossier.resolution.booth_numbers.includes(target.booth_number);
    if (!sameAccount || !sameExhibitor || !sameBooth) return;

    const recordKey =
      dossier.resolution.account_id ?? target.account_id ?? target.exhibitor_id ?? target.booth_number ?? "";
    if (restoredJumpForRef.current === recordKey) return;
    restoredJumpForRef.current = recordKey;

    const fromTab = coreSectionId(target.tab);
    if (fromTab && isCompanyJumpId(fromTab)) rememberJump(fromTab);
    const section = (fromTab && isCompanyJumpId(fromTab) ? fromTab : null) ?? lastCompanyJump;
    if (section) {
      scrollToCompanySection(section, "auto");
      return;
    }
    const scroller = document.querySelector(".company-shell");
    if (scroller instanceof HTMLElement) scroller.scrollTo({ top: 0 });
  }, [
    state.status,
    dossier,
    target.tab,
    target.account_id,
    target.exhibitor_id,
    target.booth_number,
    rememberJump,
  ]);

  const classification = dossier?.classification ?? null;
  const name =
    asText(dossier?.account?.name) ||
    str(dossier?.exhibitor, "company_name") ||
    "Company";
  const displayName = localAccountName ?? name;
  const domain = asText(dossier?.account?.domain);
  const website = httpsUrl(dossier?.profile?.website) || httpsUrl(str(dossier?.exhibitor, "website"));
  const siteHref = website || httpsUrl(domain);
  const siteLabel = domain || hostnameFromHref(siteHref);
  const secondaryHref = httpsUrl(dossier?.profile?.secondary_website);
  const secondaryLabel = hostnameFromHref(secondaryHref);
  const storedPriority = asText(classification?.priority_score) || asText(classification?.priority) || asText(dossier?.ai_rating);
  const priority = localPriority ?? storedPriority ?? "";
  const storedSegment = asText(classification?.segment_primary);
  const segment = localSegment ?? storedSegment ?? "";
  const adjacency = asText(classification?.competitive_adjacency) ?? "none";
  const booths = dossier?.resolution.booth_numbers ?? [];
  const checkInBooth = target.booth_number || booths[0] || null;
  const checkInHall = checkInBooth ? hallIdForBooth(dossier, checkInBooth) : undefined;
  const canEditClassification = Boolean(actor && accountId);
  const boothGroup = dossier && usableBoothGroup(dossier.booth_group) ? dossier.booth_group : null;
  const walkEligible = accountSupportsWalkPlan(dossier?.account?.account_types);

  const closeHeaderMenu = useCallback(() => setHeaderMenu(null), []);

  const patchClassification = useCallback(
    async (patch: Record<string, unknown>) => {
      if (!accountId || !actor) return;
      setClassPatchError(null);
      let current = { ...(classification ?? {}) };
      try {
        const got = await callTool<{ classification?: Record<string, unknown> | null }>("classification_get", {
          account_id: accountId,
        });
        if (got.classification && typeof got.classification === "object") {
          current = got.classification;
        }
      } catch {
        // First classification for this account, or get is unavailable — upsert with what we have.
      }
      await callTool("classification_upsert", {
        account_id: accountId,
        classification: { ...current, ...patch },
        actor,
        lineage: ratingLineage(),
        source: "company-browser",
      });
      onClosedOut?.();
      setReloadTick((value) => value + 1);
    },
    [accountId, actor, classification, onClosedOut],
  );

  async function saveAccount(values: { name: string; domain: string; website: string; secondaryWebsite: string } = accountForm) {
    if (!accountId || !actor) return;
    const nextName = values.name.trim();
    if (!nextName) {
      setAccountEditError("Account name is required.");
      return;
    }
    setAccountEditBusy(true);
    setAccountEditError(null);
    try {
      await callTool("account_upsert", {
        account_id: accountId,
        name: nextName,
        domain: values.domain.trim() || undefined,
        website: values.website.trim() || undefined,
        secondary_website: values.secondaryWebsite.trim(),
        actor,
        lineage: ratingLineage(),
        source: "company-browser",
      });
      setLocalAccountName(nextName);
      setAccountEditOpen(false);
      setReloadTick((value) => value + 1);
      onClosedOut?.();
    } catch (error) {
      setAccountEditError(error instanceof Error ? error.message : "account_upsert failed");
    } finally {
      setAccountEditBusy(false);
    }
  }

  const Frame = variant === "pane" ? CompanyBrowserPane : CompanyBrowserModal;

  return (
    <Frame title={displayName} onClose={onClose}>
      <header className="company-modal-header">
        {boothGroup && onSwitchAccount ? (
          <nav className="chip-row company-booth-mates" aria-label="Companies at this booth">
            {boothGroup.accounts.map((row) => {
              const selected = row.account_id === accountId;
              const label = `${shortCompanyChipLabel(row.name)}${row.is_primary ? " *" : ""}`;
              return (
                <FilterPill
                  key={row.account_id}
                  label={label}
                  pressed={selected}
                  ariaLabel={
                    selected
                      ? `${label}, current company at booth ${boothGroup.booth_number}`
                      : `Show ${label} at booth ${boothGroup.booth_number}`
                  }
                  onClick={() => {
                    if (selected) return;
                    onSwitchAccount({
                      account_id: row.account_id,
                      booth_number: boothGroup.booth_number,
                    });
                  }}
                />
              );
            })}
          </nav>
        ) : null}
        <div className="company-modal-heading">
          <div className="company-modal-identity">
            <h2>{displayName}</h2>
            <div className="company-header-meta">
              {siteLabel && siteHref ? (
                <a className="company-header-url" href={siteHref} target="_blank" rel="noreferrer">
                  {siteLabel}
                </a>
              ) : siteLabel ? (
                <span className="company-header-url">{siteLabel}</span>
              ) : null}
              {secondaryHref && secondaryLabel ? (
                <a
                  className="company-header-url"
                  href={secondaryHref}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={`Secondary website ${secondaryLabel}`}
                  title="Secondary website"
                >
                  Secondary · {secondaryLabel}
                </a>
              ) : null}
              <div className="company-header-pills">
                {booths.map((booth) => (
                  <BoothNavButton
                    key={booth}
                    booth={booth}
                    variant="chip"
                    shared={Boolean(boothGroup && boothGroup.booth_number === booth)}
                    onClick={
                      onSetDestination
                        ? () => onSetDestination(booth, displayName, hallIdForBooth(dossier, booth))
                        : undefined
                    }
                  />
                ))}
                {dossier ? (
                  <>
                    <PriorityPill
                      value={priority}
                      open={headerMenu === "priority"}
                      onOpen={() => setHeaderMenu("priority")}
                      onClose={closeHeaderMenu}
                      disabled={!canEditClassification}
                      onSelect={(next) => {
                        setLocalPriority(next);
                        const score = Number(next);
                        void patchClassification({
                          priority_score: score,
                          priority: score,
                          ai_rating: score,
                        }).catch((error) => {
                          setLocalPriority(null);
                          setClassPatchError(error instanceof Error ? error.message : "Priority update failed");
                        });
                      }}
                    />
                    <SegmentPill
                      value={segment}
                      open={headerMenu === "segment"}
                      onOpen={() => setHeaderMenu("segment")}
                      onClose={closeHeaderMenu}
                      disabled={!canEditClassification}
                      onSelect={(next) => {
                        setLocalSegment(next);
                        void patchClassification({ segment_primary: next }).catch((error) => {
                          setLocalSegment(null);
                          setClassPatchError(error instanceof Error ? error.message : "Segment update failed");
                        });
                      }}
                    />
                  </>
                ) : null}
              </div>
            </div>
          </div>
          <div className="company-modal-actions">
            {accountId && actor && !accountEditOpen ? (
              <button type="button" className="secondary" onClick={() => setAccountEditOpen(true)}>
                Edit
              </button>
            ) : null}
            {accountId && actor && accountEditOpen ? (
              <button
                type="button"
                className="secondary"
                onClick={() => {
                  setAccountEditOpen(false);
                  setAccountEditError(null);
                  setLocalAccountName(null);
                  const nextName = asText(dossier?.account?.name) || str(dossier?.exhibitor, "company_name") || "";
                  setAccountForm({
                    name: nextName,
                    domain: asText(dossier?.account?.domain) || "",
                    website: httpsUrl(dossier?.profile?.website) || httpsUrl(str(dossier?.exhibitor, "website")) || "",
                    secondaryWebsite: httpsUrl(dossier?.profile?.secondary_website) || "",
                  });
                }}
              >
                Cancel
              </button>
            ) : null}
            {accountId && actor && walkEligible ? (
              <WalkPlanButton
                on={walking}
                busy={walkBusy}
                size={16}
                onToggle={() => {
                  setWalkBusy(true);
                  setWalkError(null);
                  const next = !walking;
                  void putMyWalk(accountId, actor, next)
                    .then(() => {
                      setWalking(next);
                      onClosedOut?.();
                    })
                    .catch((error) => {
                      setWalkError(error instanceof Error ? error.message : "Walk update failed");
                    })
                    .finally(() => setWalkBusy(false));
                }}
              />
            ) : null}
            {onCheckIn ? (
              <button
                type="button"
                className="secondary"
                disabled={!checkInBooth || checkInBusy}
                title={checkInBooth ? `Check in at booth ${checkInBooth}` : "No booth to check in at"}
                onClick={() => {
                  if (!checkInBooth || !onCheckIn) return;
                  setCheckInBusy(true);
                  setCheckInError(null);
                  void Promise.resolve(onCheckIn(checkInBooth, checkInHall))
                    .then(() => setCheckedIn(true))
                    .catch((error) => {
                      setCheckInError(error instanceof Error ? error.message : "Check-in failed");
                    })
                    .finally(() => setCheckInBusy(false));
                }}
              >
                {checkInBusy ? "Checking in…" : checkedIn ? "Checked in" : "Check in"}
              </button>
            ) : null}
          <button type="button" className="secondary" onClick={onClose}>
            Close
          </button>
          </div>
        </div>
        {checkInError ? <p className="err">{checkInError}</p> : null}
        {walkError ? <p className="err">{walkError}</p> : null}
        {classPatchError ? <p className="err">{classPatchError}</p> : null}
        {accountEditError ? <p className="err">{accountEditError}</p> : null}
        {dossier ? (
          <nav className="company-section-nav" aria-label="Company sections">
            {COMPANY_SECTION_JUMPS.map((item, index) => (
              <span key={item.id} className="company-section-nav-item">
                {index > 0 ? (
                  <span className="company-section-nav-sep" aria-hidden="true">
                    |
                  </span>
                ) : null}
                <a
                  href={`#company-section-${item.id}`}
                  className={activeJump === item.id ? "is-active" : undefined}
                  aria-current={activeJump === item.id ? "true" : undefined}
                  onClick={(event) => {
                    event.preventDefault();
                    rememberJump(item.id);
                    scrollToCompanySection(item.id);
                  }}
                >
                  {item.label}
                </a>
              </span>
            ))}
          </nav>
        ) : null}
      </header>
      <div className="company-modal-body">
        {dossier?.resolution.state === "catalog_only" ? (
          <p className="company-banner">
            This exhibitor is not linked to a marketing account yet. Showing show directory only.
          </p>
        ) : null}
        {accountId && actor && accountEditOpen ? (
          <section className="contact-section">
            <h3>Account details</h3>
            <QuickAccountForm
              busy={accountEditBusy}
              error={accountEditError}
              initialValues={accountForm}
              submitLabel="Save account"
              onSubmit={(values) => {
                setAccountForm(values);
                void saveAccount(values);
              }}
            />
          </section>
        ) : null}
        {dossier ? (
          <CorePage
            dossier={dossier}
            adjacency={adjacency}
            actor={actor}
            onWalkingChange={(next) => {
              setWalking(next);
              setReloadTick((value) => value + 1);
              onClosedOut?.();
            }}
            onOpenContact={onOpenContact}
          />
        ) : (
          <StatusBlock state={state} loadingLabel="Loading company…" />
        )}
      </div>
    </Frame>
  );
}

function CorePage({
  dossier,
  adjacency,
  actor,
  onWalkingChange,
  onOpenContact,
}: {
  dossier: CompanyDossier;
  adjacency: string;
  actor?: string;
  onWalkingChange?: (walking: boolean) => void;
  onOpenContact?: (target: { contact_id: string }) => void;
}) {
  const accountId = dossier.resolution.account_id;
  const boothNumber = dossier.resolution.booth_numbers[0] ?? null;
  return (
    <div className="company-core">
      <section className="company-section" id="company-section-talktrack">
        <h3 className="company-section-heading">Talk track</h3>
        <ShowKitTab talktrack={dossier.talktrack} adjacency={adjacency} />
      </section>
      <section className="company-section" id="company-section-ratings">
        <h3 className="company-section-heading">Team ratings</h3>
        <RatingsTab classification={dossier.classification} />
      </section>
      <section className="company-section" id="company-section-overview">
        <h3 className="company-section-heading">Overview</h3>
        <OverviewTab dossier={dossier} />
      </section>
      <section className="company-section" id="company-section-services">
        <h3 className="company-section-heading">Services</h3>
        <ServicesTab dossier={dossier} />
      </section>
      <section className="company-section" id="company-section-people">
        <h3 className="company-section-heading">People</h3>
        <PeopleTab rows={dossier.contacts} onOpenContact={onOpenContact} />
      </section>
      <section className="company-section" id="company-section-sites">
        <h3 className="company-section-heading">Sites</h3>
        <SitesTab rows={dossier.sites} />
      </section>
      <section className="company-section" id="company-section-aircraft">
        <h3 className="company-section-heading">Aircraft</h3>
        <AircraftTab rows={dossier.aircraft ?? []} aircraftCount={dossier.meta?.aircraft_count} />
      </section>
      <section className="company-section" id="company-section-news">
        <h3 className="company-section-heading">News</h3>
        <NewsTab rows={dossier.news} />
      </section>
      <section className="company-section" id="company-section-notes">
        <h3 className="company-section-heading">Notes</h3>
        <NotesTab
          rows={dossier.notes}
          accountId={accountId}
          boothNumber={boothNumber}
          actor={actor}
          walkEligible={accountSupportsWalkPlan(dossier.account?.account_types)}
          onWalkingChange={onWalkingChange}
        />
      </section>
      <section className="company-section" id="company-section-techstack">
        <h3 className="company-section-heading">Tech stack</h3>
        <TechStackTab dossier={dossier} />
      </section>
      <section className="company-section" id="company-section-partnerships">
        <h3 className="company-section-heading">Partnerships</h3>
        <PartnershipsTab dossier={dossier} />
      </section>
    </div>
  );
}

function KvList({ rows }: { rows: [string, string][] }) {
  return (
    <dl className="company-kv">
      {rows.map(([label, value]) => (
        <div className="kv" key={label}>
          <dt>{label}</dt>
          <dd>{value}</dd>
        </div>
      ))}
    </dl>
  );
}

function ServicesTab({ dossier }: { dossier: CompanyDossier }) {
  const offerings = asDisplayList(dossier.profile.offerings);
  if (!offerings.length) return <p className="muted">No services stored for this account.</p>;
  return (
    <ul className="company-list">
      {offerings.map((item) => (
        <li key={item}>{item}</li>
      ))}
    </ul>
  );
}

function TechStackTab({ dossier }: { dossier: CompanyDossier }) {
  const view = parseTechStackView(dossier.profile, dossier.account?.profile);
  if (!view.softwareUsed.length && !view.systemsUsed.length) {
    return <p className="muted">No tech stack stored for this account.</p>;
  }
  return (
    <div className="company-stack">
      {view.unverified ? <p className="muted">from public web (unverified)</p> : null}
      <TechStackGroup heading="Software used" rows={view.softwareUsed} unverifiedSection={view.unverified} />
      <TechStackGroup heading="Systems used" rows={view.systemsUsed} unverifiedSection={view.unverified} />
    </div>
  );
}

function TechStackGroup({
  heading,
  rows,
  unverifiedSection,
}: {
  heading: string;
  rows: TechStackEntry[];
  unverifiedSection: boolean;
}) {
  if (!rows.length) return null;
  return (
    <div className="company-stack">
      <h4 className="company-subhead">{heading}</h4>
      <ul className="company-list">
        {rows.map((row, index) => {
          const meta = [row.category, row.role, row.confidence].filter(Boolean);
          const unverified = !unverifiedSection && row.reviewed === false;
          return (
            <li key={`${row.name}-${index}`}>
              {row.name}
              {meta.length || unverified ? (
                <div className="muted">
                  {meta.join(" · ")}
                  {unverified ? `${meta.length ? " · " : ""}from public web (unverified)` : ""}
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function PartnershipsTab({ dossier }: { dossier: CompanyDossier }) {
  const rows: [string, string][] = [];
  const partners = asStringList(dossier.profile.partnerships);
  const partnersText = asText(dossier.profile.partnerships);
  if (partners.length) rows.push(["Partnerships", partners.join(" · ")]);
  else if (partnersText) rows.push(["Partnerships", partnersText]);
  const types = dossier.account?.account_types ?? [];
  if (types.length) rows.push(["Account types", types.join(" · ")]);
  const ownership = asText(dossier.profile.ownership);
  if (ownership) rows.push(["Ownership", ownership]);
  const tin = asText(dossier.classification?.tin_track);
  if (tin && tin !== "none") rows.push(["TIN", tin]);
  const avios = asText(dossier.classification?.avios_role);
  if (avios && avios !== "none") rows.push(["Avios role", avios]);
  if (dossier.classification?.ipp_eligible === true) {
    rows.push(["IPP", "Eligible"]);
    const stage = asText(dossier.classification?.ipp_stage);
    if (stage) rows.push(["IPP stage", stage]);
    const category = asText(dossier.classification?.ipp_category);
    if (category) rows.push(["IPP category", category]);
  }
  if (!rows.length) return <p className="muted">No partnerships stored for this account.</p>;
  return <KvList rows={rows} />;
}

function strategicPriorityLines(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.flatMap((item) => {
      if (typeof item === "string" && item.trim()) return [displayPriorityLabel(item)];
      if (item && typeof item === "object") {
        const row = item as Record<string, unknown>;
        const label = typeof row.label === "string" ? row.label : typeof row.id === "string" ? row.id : "";
        return label.trim() ? [displayPriorityLabel(label)] : [];
      }
      return [];
    });
  }
  if (value && typeof value === "object") return [JSON.stringify(value)];
  return [];
}

function OverviewTab({ dossier }: { dossier: CompanyDossier }) {
  const summary = asText(dossier.profile.summary);
  const priorities = strategicPriorityLines(dossier.account?.strategic_priorities);
  const footprint = asText(dossier.profile.footprint_summary);
  const description = asText(dossier.profile.description);
  const employeeCount = dossier.profile.employee_count;
  const aircraftCount =
    typeof dossier.profile.aircraft_count === "number"
      ? dossier.profile.aircraft_count
      : typeof dossier.meta?.aircraft_count === "number"
        ? dossier.meta.aircraft_count
        : (dossier.aircraft?.length ?? 0);
  const hq = dossier.profile.hq && typeof dossier.profile.hq === "object" && !Array.isArray(dossier.profile.hq)
    ? (dossier.profile.hq as Record<string, unknown>)
    : null;
  const directory = str(dossier.exhibitor, "description");
  const hqLine = hq
    ? [hq.name, hq.street, hq.city, hq.state_region, hq.postal, hq.country, hq.airport].filter((part) => asText(part)).join(" · ")
    : "";
  return (
    <div className="company-stack">
      {summary ? <p>{summary}</p> : <p className="muted">No account profile summary stored.</p>}
      {priorities.length ? (
        <p>
          <span className="muted">Strategic priorities: </span>
          {priorities.join(" · ")}
        </p>
      ) : null}
      {description ? <p>{description}</p> : null}
      {footprint ? <p className="muted">{footprint}</p> : null}
      {typeof employeeCount === "number" ? <p className="muted">{employeeCount} employees</p> : null}
      {aircraftCount > 0 ? <p className="muted">{aircraftCount} aircraft in fleet</p> : null}
      {hqLine ? <p className="muted">HQ {hqLine}</p> : null}
      {asText(dossier.classification?.priority_rationale) ? (
        <p>{asText(dossier.classification?.priority_rationale)}</p>
      ) : null}
      {directory ? (
        <details className="catalog-block">
          <summary>Show directory</summary>
          <p className="exhibitor-copy">{directory}</p>
        </details>
      ) : null}
    </div>
  );
}

function RatingsTab({ classification }: { classification: Record<string, unknown> | null }) {
  if (!classification) {
    return <p className="muted">No classification stored for this account.</p>;
  }
  const rows: [string, string][] = [];
  const push = (label: string, value: unknown, hideFalse = false) => {
    if (hideFalse && (value === false || value == null || value === "")) return;
    if (value === false && hideFalse) return;
    const text = Array.isArray(value) ? value.join(", ") : asText(value);
    if (!text) return;
    rows.push([label, text]);
  };
  push("Priority", classification.priority_score ?? classification.priority);
  push("Rationale", classification.priority_rationale);
  push("Segment", classification.segment_primary);
  push("Adjacency", classification.competitive_adjacency);
  push("Adjacency reason", classification.adjacency_reason ?? classification.overlap_rationale);
  push("Overlap modules", classification.overlap_modules);
  push("TIN", classification.tin_track);
  push("Avios role", classification.avios_role);
  push("IPP eligible", classification.ipp_eligible, true);
  push("IPP stage", classification.ipp_stage, true);
  push("Displace", classification.displacement_risk, true);
  push("Watch", classification.competitor_watchlist, true);
  push("Lineage", classification.lineage);
  push("Confidence", classification.confidence);
  return rows.length ? <KvList rows={rows} /> : <p className="muted">No classification stored for this account.</p>;
}

function PeopleTab({
  rows,
  onOpenContact,
}: {
  rows: Record<string, unknown>[];
  onOpenContact?: (target: { contact_id: string }) => void;
}) {
  if (!rows.length) return <p className="muted">No people loaded yet.</p>;
  return (
    <table className="company-table">
      <thead>
        <tr>
          <th>Name</th>
          <th>Title</th>
          <th>Email</th>
          <th>Phone</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row, index) => {
          const contactId = asText(row.contact_id);
          const name = [asText(row.first_name), asText(row.last_name)].filter(Boolean).join(" ") || "—";
          return (
            <tr key={contactId ?? String(index)}>
              <td>
                {contactId && onOpenContact ? (
                  <button type="button" className="ghost" onClick={() => onOpenContact({ contact_id: contactId })}>
                    {name}
                  </button>
                ) : (
                  name
                )}
              </td>
              <td>{asText(row.title) ?? "—"}</td>
              <td>{asText(row.email) ?? "—"}</td>
              <td>{asText(row.phone) ?? "—"}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function NewsTab({ rows }: { rows: Record<string, unknown>[] }) {
  if (!rows.length) return <p className="muted">No taxonomy-passing news stored for this account.</p>;
  return (
    <ul className="company-list">
      {rows.map((row, index) => {
        const url = httpsUrl(row.url);
        return (
          <li key={asText(row.news_id) ?? String(index)}>
            {url ? (
              <a href={url} target="_blank" rel="noreferrer">
                {asText(row.title) ?? url}
              </a>
            ) : (
              asText(row.title)
            )}
            <div className="muted">
              {asText(row.published_at) ?? ""} {asText(row.relevance) ? `· ${asText(row.relevance)}` : ""} · T3 unverified
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function SitesTab({ rows }: { rows: Record<string, unknown>[] }) {
  if (!rows.length) return <p className="muted">No sites stored.</p>;
  return (
    <ul className="company-list">
      {rows.map((row, index) => (
        <li key={asText(row.site_id) ?? String(index)}>
          {asText(row.name) ?? "Site"}
          <div className="muted">
            {[asText(row.street), asText(row.city), asText(row.state_region), asText(row.postal), asText(row.country), asText(row.airport), asText(row.site_type)]
              .filter(Boolean)
              .join(" · ")}
          </div>
        </li>
      ))}
    </ul>
  );
}

function AircraftTab({
  rows,
  aircraftCount,
}: {
  rows: Record<string, unknown>[];
  aircraftCount?: number;
}) {
  const count = aircraftCount ?? rows.length;
  if (!rows.length) {
    return <p className="muted">No aircraft stored for this account.</p>;
  }
  return (
    <div className="company-stack">
      {count > 0 ? <p className="muted">{count} aircraft</p> : null}
      <table className="company-table">
        <thead>
          <tr>
            <th>Tail</th>
            <th>Type</th>
            <th>Serial</th>
            <th>Year</th>
            <th>Base</th>
            <th>Seats</th>
            <th>Notes</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => {
            const raw = row.raw_attributes;
            const extra =
              raw && typeof raw === "object" && !Array.isArray(raw)
                ? Object.entries(raw as Record<string, unknown>)
                    .map(([key, value]) => `${key}: ${asText(value) ?? String(value)}`)
                    .join(" · ")
                : "";
            const noteParts = [
              asText(row.manufacturer),
              asText(row.management_company) ? `Mgmt: ${asText(row.management_company)}` : null,
              asText(row.notes),
              extra,
            ].filter(Boolean);
            return (
              <tr key={asText(row.aircraft_id) ?? String(index)}>
                <td>{asText(row.tail_number) ?? "—"}</td>
                <td>{asText(row.make_model) ?? "—"}</td>
                <td>{asText(row.serial_number) ?? "—"}</td>
                <td>{asText(row.year_manufactured) ?? "—"}</td>
                <td>{asText(row.base_airport) ?? "—"}</td>
                <td>{asText(row.seats) ?? "—"}</td>
                <td>{noteParts.length ? noteParts.join(" · ") : "—"}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function NotesTab({
  rows,
  accountId,
  boothNumber,
  actor,
  onNoteSaved,
  walkEligible = false,
  onWalkingChange,
}: {
  rows: Record<string, unknown>[];
  accountId?: string | null;
  boothNumber?: string | null;
  actor?: string;
  onNoteSaved?: () => void;
  walkEligible?: boolean;
  onWalkingChange?: (walking: boolean) => void;
}) {
  const [body, setBody] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<"note" | "walk" | null>(null);
  const [askWalk, setAskWalk] = useState(false);
  const [history, setHistory] = useState(rows);
  const appendTranscript = useCallback((text: string) => {
    setBody((current) => {
      const prefix = current.trimEnd();
      return prefix ? `${prefix} ${text}` : text;
    });
  }, []);
  const speech = useSpeechToText(appendTranscript);

  useEffect(() => {
    setHistory(rows);
  }, [rows]);

  async function saveNote() {
    if (!accountId || !actor) return;
    if (!body.trim()) {
      setError("Write a note before saving.");
      return;
    }
    setBusy("note");
    setError(null);
    try {
      await callTool("note_add", {
        body: body.trim(),
        note_type: boothNumber ? "booth_touch" : undefined,
        booth_number: boothNumber || undefined,
        account_id: accountId,
        actor,
        lineage: ratingLineage(),
      });
      const listed = await callTool<{ results?: Record<string, unknown>[] }>("note_list", {
        account_id: accountId,
        limit: 50,
      }).catch(() => null);
      setHistory(listed?.results ?? [{ body: body.trim(), actor, created_at: new Date().toISOString() }, ...history]);
      setBody("");
      if (walkEligible) setAskWalk(true);
      onNoteSaved?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : "note_add failed");
    } finally {
      setBusy(null);
    }
  }

  async function chooseWalk(walk: boolean) {
    if (!accountId || !actor) return;
    setBusy("walk");
    setError(null);
    try {
      await putMyWalk(accountId, actor, walk);
      setAskWalk(false);
      onWalkingChange?.(walk);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Walk update failed");
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="company-stack">
      {accountId && actor ? (
        askWalk ? (
          <ContinueWalkPrompt busy={busy === "walk"} error={error} onChoose={(walk) => void chooseWalk(walk)} />
        ) : (
          <form
            className="closeout-card"
            onSubmit={(event) => {
              event.preventDefault();
              void saveNote();
            }}
          >
            <label className="field">
              <span className="visually-hidden">Note</span>
              <div className="note-entry-wrap">
                <textarea
                  rows={3}
                  value={body}
                  onChange={(event) => setBody(event.target.value)}
                  placeholder="Booth conversation, next step, context…"
                />
                <button
                  type="button"
                  className={`note-entry-mic${speech.listening ? " on" : ""}`}
                  disabled={!speech.available}
                  aria-label={speech.listening ? "Stop microphone" : "Voice to text"}
                  title={speech.available ? (speech.listening ? "Stop microphone" : "Voice to text") : "Speech not available in this browser"}
                  aria-pressed={speech.listening}
                  onClick={speech.toggle}
                >
                  <MicIcon />
                </button>
              </div>
            </label>
            {error ? <p className="err">{error}</p> : null}
            <div className="actions closeout-actions">
              <button type="submit" disabled={Boolean(busy)}>
                {busy === "note" ? "Saving…" : "Save note"}
              </button>
            </div>
            {!speech.available ? <p className="muted">Speech not available in this browser</p> : null}
          </form>
        )
      ) : (
        <div className="voice-placeholder">
          <p className="muted">Sign in to add a note on this account.</p>
        </div>
      )}
      {history.length ? (
        <ul className="company-list">
          {history.map((row, index) => (
            <li key={asText(row.note_id) ?? String(index)}>
              <div>{asText(row.body) ?? "—"}</div>
              <div className="muted">
                {[asText(row.note_type), asText(row.created_at) ? formatNoteStamp(asText(row.created_at)) : null, asText(row.actor)]
                  .filter(Boolean)
                  .join(" · ")}
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <p className="muted">No notes stored for this account.</p>
      )}
    </div>
  );
}

function ShowKitTab({
  talktrack,
  adjacency,
}: {
  talktrack: Record<string, unknown> | null;
  adjacency: string;
}) {
  const competitor = adjacency === "direct" || adjacency === "overlapping";
  return (
    <div className="company-stack">
      {competitor ? (
        <p className="company-banner">Direct / overlapping competitor — capture only; no IPP pitch.</p>
      ) : null}
      {talktrack && asText(talktrack.talk_track) ? (
        <p>{asText(talktrack.talk_track)}</p>
      ) : (
        <p className="muted">No approved talk track for outbound use.</p>
      )}
    </div>
  );
}
